/**
 * P8 Phase C — Public Document Upload Portal.
 * Route: /portal/upload/:token
 *
 * Token-based access (no admin auth). Lead uploads each requested KYC
 * document, sees status feedback in real time and a per-doc rejection
 * note where applicable.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  ShieldCheck, Upload, CheckCircle2, XCircle, Loader2, FileText,
  AlertTriangle, Lock, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending:  'bg-slate-200 text-slate-700',
  uploaded: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

const KIND_LABEL = {
  trade_license:       'Trade License',
  trn_certificate:     'TRN Certificate',
  eid:                 'Emirates ID',
  passport:            'Passport',
  cert_incorporation:  'Certificate of Incorporation',
  tax_id:              'Tax ID',
  bank_letter:         'Bank Letter',
  moa:                 'Memorandum of Association',
  ubo_declaration:     'UBO Declaration',
  source_of_funds:     'Source of Funds',
  nda:                 'NDA',
  other:               'Other',
};

const ALLOWED_TYPES = '.pdf,.png,.jpg,.jpeg,.webp';

const UploadDocuments = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [busyKind, setBusyKind] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErrorMsg(null);
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/public/portal/${token}`);
      setData(r.data);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || 'This upload link is invalid or expired.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind); setFeedback(null);
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    try {
      await axios.post(
        `${API_URL}/api/onboarding/public/portal/${token}/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      await load();
      setFeedback({ kind: 'success', text: `${KIND_LABEL[kind] || kind} uploaded successfully.` });
    } catch (e) {
      setFeedback({
        kind: 'error',
        text: e?.response?.data?.detail || 'Upload failed. Please try again.',
      });
    } finally { setBusyKind(null); }
  };

  if (loading) {
    return (
      <Centered>
        <Loader2 className="w-6 h-6 animate-spin text-[#C9A86A]" />
        <div className="text-sm text-slate-300 mt-3">Loading your portal…</div>
      </Centered>
    );
  }

  if (errorMsg || !data) {
    return (
      <Centered>
        <Lock className="w-12 h-12 text-rose-400" />
        <div className="text-2xl font-serif text-white mt-4" data-testid="portal-error-title">Link unavailable</div>
        <div className="text-sm text-slate-400 mt-2 max-w-md text-center">{errorMsg}</div>
      </Centered>
    );
  }

  const { submission, documents, expires_at: expiresAt } = data;
  const required = (documents || []).filter(d => d.required);
  const approvedCount = required.filter(d => d.status === 'approved').length;
  const allDone = required.length > 0 && approvedCount === required.length;

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white pb-24"
      data-testid="portal-upload-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-10">
          <ShieldCheck className="w-7 h-7 text-[#C9A86A]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A86A]">
              Grupo BMP — Compliance Portal
            </div>
            <div className="text-xl font-serif">Document Upload</div>
          </div>
        </div>

        {/* Submission summary */}
        <div
          className="p-5 rounded-2xl border border-white/10 bg-white/5 mb-8 backdrop-blur-sm"
          data-testid="portal-submission-summary">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Registration</div>
          <div className="text-2xl font-serif mt-0.5">{submission?.legal_name}</div>
          <div className="text-sm text-slate-300 mt-1">
            {submission?.contact_name} · <span className="font-mono">{submission?.contact_email}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-2">
            <Clock className="w-3 h-3" />
            Portal expires {(expiresAt || '').slice(0, 10)}
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm uppercase tracking-[0.18em] text-slate-300">
            {approvedCount} of {required.length} approved
          </div>
          {allDone && (
            <Badge className="bg-emerald-600 text-white text-[10px] uppercase">
              <CheckCircle2 className="w-3 h-3 inline mr-1" /> Complete
            </Badge>
          )}
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-8">
          <div
            data-testid="portal-progress-bar"
            className="h-full bg-[#C9A86A] transition-all"
            style={{ width: `${required.length ? (approvedCount * 100 / required.length) : 0}%` }}
          />
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            data-testid="portal-feedback"
            className={`mb-6 px-4 py-3 rounded-lg text-sm border ${
              feedback.kind === 'success'
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-200'
            }`}>
            {feedback.kind === 'success'
              ? <CheckCircle2 className="w-4 h-4 inline mr-2" />
              : <AlertTriangle className="w-4 h-4 inline mr-2" />}
            {feedback.text}
          </div>
        )}

        {/* Documents */}
        <ul className="space-y-3" data-testid="portal-doc-list">
          {required.map(d => (
            <li
              key={d.id}
              data-testid={`portal-doc-${d.kind}`}
              className="p-4 rounded-xl border border-white/10 bg-white/[0.03]">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="w-4 h-4 text-[#C9A86A]" />
                    <span className="font-medium">{KIND_LABEL[d.kind] || d.kind}</span>
                    <Badge className={`${STATUS_BADGE[d.status]} text-[10px] uppercase`}>{d.status}</Badge>
                  </div>
                  {d.filename && (
                    <div className="text-xs text-slate-400 mt-1 font-mono truncate">
                      {d.filename}
                    </div>
                  )}
                  {d.rejection_reason && (
                    <div
                      data-testid={`portal-doc-${d.kind}-rejected`}
                      className="mt-2 text-xs px-2.5 py-1.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-200">
                      <XCircle className="w-3 h-3 inline mr-1" />
                      Rejected: {d.rejection_reason}
                    </div>
                  )}
                </div>

                {(d.status === 'pending' || d.status === 'rejected') && (
                  <label
                    htmlFor={`upload-${d.id}`}
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C9A86A] text-black text-sm font-medium hover:bg-[#B89860] transition-colors disabled:opacity-50">
                    {busyKind === d.kind
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Upload className="w-4 h-4" />}
                    {d.status === 'rejected' ? 'Re-upload' : 'Upload'}
                    <input
                      id={`upload-${d.id}`}
                      data-testid={`portal-doc-${d.kind}-input`}
                      type="file"
                      accept={ALLOWED_TYPES}
                      className="hidden"
                      disabled={!!busyKind}
                      onChange={(e) => upload(d.kind, e.target.files?.[0])}
                    />
                  </label>
                )}
                {d.status === 'uploaded' && (
                  <span className="text-xs text-amber-300 self-center">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Awaiting review
                  </span>
                )}
                {d.status === 'approved' && (
                  <span className="text-xs text-emerald-300 self-center">
                    <CheckCircle2 className="w-3 h-3 inline mr-1" />
                    Approved
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="text-[11px] text-slate-500 mt-10 text-center">
          Allowed file types: PDF, JPG, PNG, WEBP · Max 25MB per file<br />
          Files are encrypted at rest and shared only with our compliance team.
        </div>
      </div>
    </div>
  );
};

const Centered = ({ children }) => (
  <div className="min-h-screen bg-[#0B0B0B] text-white flex flex-col items-center justify-center px-6">
    {children}
  </div>
);

export default UploadDocuments;
